import React, { useState, useEffect } from 'react';
import speakerIcon from '../assets/Add Order Campaign.svg';
import editIcon from '../assets/Edit (1).svg';
import { orderCampaignApi, authHelpers } from '../services/api'; // Adjust path as needed
import { formatDate } from '../utils/helpers';
import { useTheme } from '../context/ThemeContext';
import { X, Trash2 } from 'lucide-react';
import { message } from 'antd';

/**
 * Toggle Component
 */
function Toggle({ checked, onChange, ariaLabel }) {
  const { currentTheme } = useTheme();
  const isDark = currentTheme.textColor === '#FFFFFF';
  return (
    <button
      type="button"
      aria-label={ariaLabel}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 border-2`}
      style={{
        backgroundColor: checked ? currentTheme.color : '#FFFFFF',
        borderColor: isDark ? '#FFFFFF' : '#D1D5DB',
        '--tw-ring-color': currentTheme.color
      }}
    >
      <span
        className={`inline-block h-5 w-5 transform rounded-full transition-transform ${checked ? 'translate-x-5 bg-white' : 'translate-x-1 bg-gray-400'
          } border-2 border-white shadow-sm`}
      />
    </button>
  );
}

/**
 * Delete Icon Component
 */
function DeleteIcon(props) {
  return (
    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" {...props}>
      <path
        d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m3 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6h14Z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}



/**
 * Main Component
 */
export default function SettingsOrderCampaign() {
  const { currentTheme } = useTheme();
  const isDark = currentTheme.textColor === '#FFFFFF';
  // State management
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [editingRow, setEditingRow] = useState(null);
  const [addForm, setAddForm] = useState({
    campaignName: '',
    status: true,
  });

  // Fetch data on component mount
  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      const campaignsData = await orderCampaignApi.getAllCampaigns();

      // Transform API response to match UI format
      const transformedRows = campaignsData.map((item) => ({
        id: item.id.toString(),
        campaignName: item.campaignName,
        status: item.status,
        createdBy: item.createdByName || 'Unknown',
        createdAt: item.createdAt,
      }));

      setRows(transformedRows);
    } catch (err) {
      console.error('Error fetching campaigns:', err);
      setError(err.message || err.error || 'Failed to load campaigns. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle adding new campaign
   */
  const handleAddSubmit = async () => {
    try {
      // Validate form
      if (!addForm.campaignName.trim()) {
        message.warning('Please enter a campaign name');
        return;
      }

      // Get current user ID from localStorage
      const currentUser = authHelpers.getCurrentUser();
      const createdById = currentUser?.id || 1;

      // Prepare data for API
      const apiData = {
        campaignName: addForm.campaignName.trim(),
        status: addForm.status,
        createdById: createdById,
      };

      // Call API
      await orderCampaignApi.createCampaign(apiData);

      // Refresh data
      await fetchData();

      // Close modal and reset form
      setIsAddOpen(false);
      setAddForm({
        campaignName: '',
        status: true,
      });

      message.success('Campaign created successfully!');
    } catch (err) {
      console.error('Error creating campaign:', err);
      message.error(err.message || err.error || 'Failed to create campaign. Please try again.');
    }
  };

  /**
   * Handle editing campaign
   */
  const handleEditSubmit = async () => {
    try {
      if (!editingRow) return;

      // Validate form
      if (!editingRow.campaignName.trim()) {
        message.warning('Please enter a campaign name');
        return;
      }

      // Prepare data for API
      const apiData = {
        campaignName: editingRow.campaignName.trim(),
        status: editingRow.status,
      };

      // Call API
      await orderCampaignApi.updateCampaign(editingRow.id, apiData);

      // Refresh data
      await fetchData();

      // Close modal
      setIsEditOpen(false);
      setEditingRow(null);

      message.success('Campaign updated successfully!');
    } catch (err) {
      console.error('Error updating campaign:', err);
      message.error(err.message || err.error || 'Failed to update campaign. Please try again.');
    }
  };

  /**
   * Handle status toggle (inline update)
   */
  const handleStatusToggle = async (rowId) => {
    try {
      const row = rows.find(r => r.id === rowId);
      if (!row) return;

      // Update local state immediately for better UX
      setRows((prev) => prev.map((r) => (r.id === rowId ? { ...r, status: !r.status } : r)));

      // Call API to toggle status
      await orderCampaignApi.toggleCampaignStatus(rowId);
    } catch (err) {
      console.error('Error toggling status:', err);
      // Revert local state on error
      await fetchData();
      message.error('Failed to update status. Please try again.');
    }
  };

  /**
   * Handle delete
   */
  const handleDelete = async (rowId, campaignName) => {
    if (!window.confirm(`Are you sure you want to delete campaign "${campaignName}"?`)) {
      return;
    }

    try {
      await orderCampaignApi.deleteCampaign(rowId);
      await fetchData();
      message.success('Campaign deleted successfully!');
    } catch (err) {
      console.error('Error deleting campaign:', err);
      message.error(err.message || err.error || 'Failed to delete campaign. Please try again.');
    }
  };

  /**
   * Open edit modal
   */
  const openEditModal = (row) => {
    setEditingRow({ ...row });
    setIsEditOpen(true);
  };

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-gray-600">Loading campaigns...</div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-64 space-y-4">
        <div className="text-lg text-red-600">{error}</div>
        <button
          onClick={fetchData}
          className="px-6 py-2 rounded-lg text-white transition-colors"
          style={{ backgroundColor: currentTheme.color }}
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className={`space-y-4 relative min-h-screen -mx-8 px-8 pb-8 transition-colors duration-300 ${isDark ? 'bg-[#0f172a]' : ''}`}>
      {/* Page Header Section */}
      <div className="sticky top-0 z-30 -mx-8 px-8 py-3" style={{ backgroundColor: `${currentTheme.color}10` }}>
        <div className="flex items-center justify-between">
          <h2 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-800'}`}>Order Campaign</h2>

          {/* Add New Campaign Button */}
          <button
            type="button"
            onClick={() => {
              setAddForm({
                campaignName: '',
                status: true,
              });
              setIsAddOpen(true);
            }}
            className="inline-flex h-14 w-14 items-center justify-center rounded-full text-white shadow-lg transition-colors"
            style={{ backgroundColor: currentTheme.color }}
            title="Add Order Campaign"
            aria-label="Add Order Campaign"
          >
            <img
              src={speakerIcon}
              alt="Add Order Campaign"
              className="h-10 w-10"
              aria-hidden="true"
            />
          </button>
        </div>
      </div>

      {/* Add New Campaign Modal */}
      {isAddOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4"
          role="dialog"
          aria-modal="true"
          aria-label="Add New Order Campaign"
          onClick={() => setIsAddOpen(false)}
        >
          <div
            className={`w-full max-w-xl overflow-hidden rounded-2xl shadow-2xl border ${isDark ? 'bg-[#1e293b] border-white/10' : 'bg-white border-white/50'}`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-6 py-4" style={{ backgroundColor: `${currentTheme.color}15` }}>
              <div className="flex items-center gap-3">
                <img
                  src={speakerIcon}
                  alt="Order Campaign"
                  className="h-12 w-12"
                  aria-hidden="true"
                />
                <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-800'}`}>Add New Order Campaign</h3>
              </div>
              <button
                type="button"
                onClick={() => setIsAddOpen(false)}
                className="p-2 rounded-lg transition-colors"
                style={{ color: currentTheme.color, hover: `bg-[${currentTheme.color}20]` }}
                aria-label="Close"
                title="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="px-6 py-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2">
                  <label className={`block text-xs font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                    Campaign Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    value={addForm.campaignName}
                    onChange={(e) => setAddForm((p) => ({ ...p, campaignName: e.target.value }))}
                    placeholder="Enter campaign name"
                    className={`w-full h-10 px-3 border rounded-lg focus:outline-none transition-all ${isDark ? 'bg-[#0f172a] border-white/10 text-white' : 'border-gray-300 text-gray-900'}`}
                    style={{ focusBorderColor: currentTheme.color }}
                    required
                  />
                </div>
              </div>

              <div className="mt-6 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setIsAddOpen(false)}
                  className={`px-6 py-2.5 rounded-xl border font-semibold transition-colors ${isDark ? 'border-white/10 text-gray-300 hover:bg-white/5' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleAddSubmit}
                  className="px-6 py-2.5 rounded-xl text-white font-semibold transition-colors"
                  style={{ backgroundColor: currentTheme.color }}
                >
                  Submit
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Campaign Modal */}
      {isEditOpen && editingRow && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4"
          role="dialog"
          aria-modal="true"
          aria-label="Edit Order Campaign"
          onClick={() => setIsEditOpen(false)}
        >
          <div
            className={`w-full max-w-xl overflow-hidden rounded-2xl shadow-2xl border ${isDark ? 'bg-[#1e293b] border-white/10' : 'bg-white border-white/50'}`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-6 py-4" style={{ backgroundColor: `${currentTheme.color}1A` }}>
              <div className="flex items-center gap-3">
                <img
                  src={speakerIcon}
                  alt="Order Campaign"
                  className="h-12 w-12"
                  aria-hidden="true"
                />
                <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-800'}`}>Edit Order Campaign</h3>
              </div>
              <button
                type="button"
                onClick={() => setIsEditOpen(false)}
                className="p-2 rounded-lg transition-colors"
                style={{ color: currentTheme.color, hover: `bg-[${currentTheme.color}20]` }}
                aria-label="Close"
                title="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="px-6 py-5">
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <label className={`block text-xs font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                    Campaign Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    value={editingRow.campaignName}
                    onChange={(e) => setEditingRow((p) => ({ ...p, campaignName: e.target.value }))}
                    placeholder="Enter campaign name"
                    className={`w-full h-10 px-3 border rounded-lg focus:outline-none transition-all ${isDark ? 'bg-[#0f172a] border-white/10 text-white' : 'border-gray-300 text-gray-900'}`}
                    style={{ focusBorderColor: currentTheme.color }}
                    required
                  />
                </div>

                <div className="flex items-center justify-between">
                  <label className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Status</label>
                  <Toggle
                    checked={editingRow.status}
                    onChange={(v) => setEditingRow((p) => ({ ...p, status: v }))}
                    ariaLabel="Toggle status"
                  />
                </div>
              </div>

              <div className="mt-6 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setIsEditOpen(false)}
                  className={`px-6 py-2.5 rounded-xl border font-semibold transition-colors ${isDark ? 'border-white/10 text-gray-300 hover:bg-white/5' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleEditSubmit}
                  className="px-6 py-2.5 rounded-xl text-white font-semibold transition-colors"
                  style={{ backgroundColor: currentTheme.color }}
                >
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main Data Table Section */}
      <div className={`${isDark ? 'bg-[#1e293b]/80 border-white/10' : 'bg-white/80 border-white/50'} backdrop-blur rounded-2xl shadow-xl border overflow-hidden`}>
        <div className="overflow-x-auto">
          <table className="min-w-[800px] w-full">
            <thead>
              <tr className="text-white" style={{ backgroundColor: currentTheme.color }}>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider whitespace-nowrap">
                  Campaign Name
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider whitespace-nowrap">
                  Status
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider whitespace-nowrap">
                  Created By
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider whitespace-nowrap">
                  Created At
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider whitespace-nowrap">
                  Actions
                </th>
              </tr>
            </thead>

            <tbody className={`divide-y ${isDark ? 'divide-white/5' : 'divide-gray-200'}`}>
              {rows.map((r, idx) => (
                <tr key={r.id} className={idx % 2 === 0 ? (isDark ? 'bg-[#1e293b]' : 'bg-white') : ''} style={{ backgroundColor: idx % 2 === 0 ? undefined : `${currentTheme.color}08` }}>
                  <td className={`px-4 py-3 text-sm whitespace-nowrap ${isDark ? 'text-gray-300' : 'text-gray-900'}`}>{r.campaignName}</td>
                  <td className="px-4 py-3">
                    <Toggle
                      checked={r.status}
                      onChange={() => handleStatusToggle(r.id)}
                      ariaLabel={`Toggle status for ${r.campaignName}`}
                    />
                  </td>
                  <td className={`px-4 py-3 text-sm whitespace-nowrap ${isDark ? 'text-gray-400' : 'text-gray-700'}`}>{r.createdBy}</td>
                  <td className={`px-4 py-3 text-sm whitespace-nowrap ${isDark ? 'text-gray-400' : 'text-gray-700'}`}>
                    {formatDate(r.createdAt)}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => openEditModal(r)}
                        className={`inline-flex items-center justify-center w-9 h-9 rounded-lg border transition-colors ${isDark ? 'border-blue-800 bg-blue-900/20 text-blue-400 hover:bg-blue-900/40' : 'border-blue-100 bg-blue-50 text-blue-600 hover:bg-blue-100'}`}
                        title="Edit"
                        aria-label={`Edit ${r.campaignName}`}
                      >
                        <img
                          src={editIcon}
                          alt="Edit"
                          className="w-5 h-5"
                          aria-hidden="true"
                          style={{ filter: isDark ? 'invert(1) brightness(2)' : 'none', opacity: 0.8 }}
                        />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDelete(r.id, r.campaignName)}
                        className={`inline-flex items-center justify-center w-9 h-9 rounded-lg border transition-colors ${isDark ? 'border-red-800 bg-red-900/20 text-red-400 hover:bg-red-900/40' : 'border-red-200 bg-red-50 text-red-600 hover:bg-red-100'}`}
                        title="Delete"
                        aria-label={`Delete ${r.campaignName}`}
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}